import { ReloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SIGNING_STATUS_LABELS, type OperatorSigning, type OperatorSigningCheck } from "@wms/domain";
import { Alert, App, Button, List, Space, Spin, Tag, Typography } from "antd";
import { Link, useParams } from "react-router-dom";
import { api, describeError } from "../api/client";
import { formatDate } from "../api/format";
import { useAuthStore } from "../auth/store";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";
import { SigningView } from "../portal/SigningView";

interface SignResult {
  side: string;
  status: string;
  package_status: string;
  verification: string;
}

/** One check before the signature: done, or the concrete reason with the place to fix it (for a role that may). */
function CheckRow({ check }: { check: OperatorSigningCheck }) {
  return (
    <List.Item data-testid={`operator-check-${check.code}`} data-ok={check.ok || undefined}>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Space wrap size={8}>
          <Tag color={check.ok ? "success" : "warning"} bordered={false}>
            {check.ok ? "Готово" : "Нужно сделать"}
          </Tag>
          <Typography.Text strong>{check.label}</Typography.Text>
        </Space>
        <Typography.Text type={check.ok ? "secondary" : undefined}>{check.text}</Typography.Text>
        {!check.ok && check.fix_path && (
          <Link to={check.fix_path} data-testid={`operator-fix-${check.code}`}>
            {check.fix_label ?? "Исправить"}
          </Link>
        )}
      </Space>
    </List.Item>
  );
}

/**
 * CW31-B16: «Подписать от исполнителя» of a frozen act — the current version (document, version and hash, the parties
 * and their states), the checks before signing (right, version, order of the sides, requisites of our organization in
 * the signed version, a provider that really works) and the signature through POST /signing/packages/{id}/sign with
 * the same `expected_hash` and provider as the client's. Nothing here changes a status in place of a signature; the
 * server checks every condition again when «Подписать эту версию» is pressed.
 */
export function ActSigningPage() {
  const { id = "" } = useParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const key = ["acts", id, "operator-signing"];
  const data = useQuery({ queryKey: key, queryFn: () => api.get<OperatorSigning>(`/signing/acts/${id}/operator`) });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["acts"] });
    queryClient.invalidateQueries({ queryKey: ["signing_packages"] });
    queryClient.invalidateQueries({ queryKey: key });
  };
  const sign = useMutation({
    mutationFn: (body: { provider: string; signature: string; signer_name?: string; expected_hash: string }) =>
      api.post<SignResult>(`/signing/packages/${data.data?.package_id}/sign`, body),
    onSuccess: (r) => {
      if (r.status === "signed") message.success(`Подпись исполнителя принята. Состояние пакета: ${SIGNING_STATUS_LABELS[r.package_status] ?? r.package_status}`);
      // an unfinished verification is never shown as a signature
      else message.warning("Подпись передана, но проверка не завершена: результат неизвестен. Повторно подписывать не нужно — сверьте попытку в карточке пакета", 8);
      refresh();
    },
    onError: (e) => {
      message.error(describeError(e), 8);
      refresh();
    },
  });

  if (data.isError) return <LoadProblem error={data.error} onRetry={() => data.refetch()} />;
  const answer = data.data;
  const act = answer?.act;
  return (
    <div>
      <PageHeader
        title={act ? `Подпись от исполнителя: АВР ${act.number}` : "Подпись от исполнителя"}
        back
        subtitle={act ? `АВР от ${formatDate(act.date)}. Сторона «Исполнитель» — наша организация; клиент подписывает свою сторону в кабинете или по ссылке.` : undefined}
        extra={
          <Space wrap>
            {act && (
              <Link to={act.path} data-testid="operator-open-act">
                <Button>Открыть АВР</Button>
              </Link>
            )}
            {answer?.package_id && (
              <Link to={`/signing_packages/${answer.package_id}`} data-testid="operator-open-package">
                <Button>Пакет в архиве подписания</Button>
              </Link>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => data.refetch()} aria-label="Обновить" />
          </Space>
        }
      />
      <div className="wms-card" style={{ padding: 16 }}>
        {!answer ? (
          <Spin />
        ) : (
          <>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Проверка перед подписью
            </Typography.Title>
            {answer.can_sign ? (
              <Alert type="success" showIcon style={{ marginBottom: 12 }} message="Подписать можно: проверьте документ, версию и хеш ниже и подпишите выбранным способом" data-testid="operator-can-sign" />
            ) : (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Подписать сейчас нельзя" description={answer.why_not} data-testid="operator-why-not" />
            )}
            <List size="small" bordered dataSource={answer.checks} renderItem={(check) => <CheckRow check={check} />} style={{ marginBottom: 16 }} />
            {answer.view ? (
              <SigningView
                view={answer.view}
                providers={answer.providers}
                canSign={answer.can_sign && can("billing.documents.sign")}
                canReject={false}
                busy={sign.isPending}
                onSign={(body) => sign.mutate(body)}
              />
            ) : (
              <Alert type="info" showIcon message="Версии для подписания ещё нет" description="Подпись станет доступна после «Подготовить к подписанию» в карточке АВР." />
            )}
          </>
        )}
      </div>
    </div>
  );
}
